// The contributor bio: a box to write it in, used on the proof page (when we have no usable bio, approving waits for it)
// and on the bio-only page that MWS Desk's bio request links to (?b=<token>).
import { useEffect, useRef, useState } from "react";
import { BIO_WORDS, type BioLookupResponse, type BioSendResponse, type ProofBioState } from "./types";

const countWords = (s: string) => s.split(/\s+/).filter(Boolean).length;

export function BioBox(props: {
  /** One of the two: the proof token, or the bio-only token. */
  auth: { token: string } | { bioToken: string };
  name: string;
  bio: ProofBioState;
  onSaved: (bio: string) => void;
}) {
  const { bio, name } = props;
  const [text, setText] = useState(bio.sent ?? bio.current);
  const [editing, setEditing] = useState(!bio.sent);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const n = countWords(text);
  const last = name.trim().split(/\s+/).at(-1) ?? "";
  const missingName = n >= BIO_WORDS.min && last.length > 1 && !text.toLowerCase().includes(last.toLowerCase());

  const save = async () => {
    if (n < BIO_WORDS.min) { setError(`A bio needs at least ${BIO_WORDS.min} words.`); return; }
    if (n > BIO_WORDS.max) { setError(`Please keep it to ${BIO_WORDS.max} words.`); return; }
    setBusy(true); setError(null);
    try {
      const res = await fetch("/api/proof/bio", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...props.auth, bio: text }),
      });
      const data = (await res.json()) as BioSendResponse;
      if (data.ok) { setText(data.bio); setEditing(false); props.onSaved(data.bio); }
      else setError(data.errors[0] ?? "We couldn't save that. Please try again.");
    } catch {
      setError("We couldn't reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!editing && bio.sent) {
    return (
      <div className="bio-box bio-box--done">
        <p className="proof-body"><strong>Thank you, your bio is in.</strong> This is what will print with the contributors:</p>
        <blockquote className="bio-sent">{bio.sent}</blockquote>
        <button type="button" className="proof-link" onClick={() => setEditing(true)}>Change it</button>
      </div>
    );
  }
  return (
    <div className="bio-box">
      <label className="proof-label" htmlFor="bio-text">Your contributor bio</label>
      <p id="bio-hint" className="proof-hint">
        Two to four sentences, about yourself in the third person, starting with your name. For example:
        “Jane Doe is a poet from Los Angeles whose work has appeared in…”
        {bio.current && !bio.sent ? " The bio we have is below; fix it up and send it." : ""}
      </p>
      <textarea id="bio-text" className="proof-textarea" rows={6} value={text} aria-describedby="bio-hint"
        onChange={(e) => setText(e.target.value)} placeholder={`${name || "Jane Doe"} is a writer from…`} />
      <p className={`bio-count${n > BIO_WORDS.max ? " over" : ""}`}>
        {n} word{n === 1 ? "" : "s"}{missingName ? ` · tip: start with your name (“${name}”)` : ""}
      </p>
      {error && <p className="proof-error" role="alert">{error}</p>}
      <div className="proof-actions">
        <button type="button" className="proof-btn proof-btn--ok" disabled={busy} onClick={() => void save()}>
          {busy ? "Saving…" : "Send my bio"}
        </button>
        {bio.sent && <button type="button" className="proof-link" disabled={busy} onClick={() => { setText(bio.sent ?? ""); setEditing(false); setError(null); }}>Cancel</button>}
      </div>
    </div>
  );
}

/** The bio-only page: ?b=<token> from the bio request email. */
export function BioView({ onBack }: { onBack: () => void }) {
  const bioToken = new URLSearchParams(window.location.search).get("b") ?? "";
  const [state, setState] = useState<BioLookupResponse | null>(null);
  const heading = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    const previous = document.title;
    document.title = "Your Bio | Method Writers Speak";
    fetch("/api/proof/bio-lookup", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ bioToken }) })
      .then((r) => r.json() as Promise<BioLookupResponse>)
      .then(setState)
      .catch(() => setState({ ok: false, reason: "invalid", errors: ["We couldn't reach the server. Please refresh to try again."] }));
    return () => { document.title = previous; };
  }, [bioToken]);
  useEffect(() => { heading.current?.focus(); }, [state]);

  return (
    <main className="page proof-page">
      <div className="proof-shell">
        <div className="proof-kicker">{state?.ok ? state.issueName.replace(/\s*#(\d+)$/, " · Issue $1") : "Method Writers Speak"}</div>
        {!state && <p className="proof-body">Loading…</p>}
        {state && !state.ok && (
          <div className="proof-message">
            <h1 ref={heading} tabIndex={-1} className="proof-title">That link didn't work</h1>
            <p className="proof-body">{state.errors[0]}</p>
            <button type="button" className="proof-link" onClick={onBack}>Back to the main site</button>
          </div>
        )}
        {state?.ok && (
          <>
            <h1 ref={heading} tabIndex={-1} className="proof-title">Your bio, <em>{state.first}</em></h1>
            {!state.bio.sent && (
              <div className="bio-warning" role="status">
                <p><strong>We need a bio from you for the contributors' pages.</strong></p>
                <p>Please send it by <strong>{state.deadlineLabel}</strong>. Without one, your work might be left out of this issue.</p>
              </div>
            )}
            <BioBox auth={{ bioToken }} name={state.name} bio={state.bio}
              onSaved={(bio) => setState({ ...state, bio: { ...state.bio, sent: bio, needed: false } })} />
            <p className="proof-exit"><button type="button" className="proof-link" onClick={onBack}>← Back to the main site</button></p>
          </>
        )}
      </div>
    </main>
  );
}
